import { Avatar, Box, Button, Checkbox, Dialog, DialogTitle, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, Tab, Tabs } from "@mui/material";
import { WhoToSplitBetween } from "./AddExpenses";
import React, { useEffect } from "react";

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { AllPeople } from "../../helpers/people";

interface Props {
  open: boolean;
  whoToSplitBetween: WhoToSplitBetween;
  manualSplitListOfPeople: string[] | null;
  onClose: () => void;
  onConfirm: (
    whoToSplitBetween: WhoToSplitBetween,
    manualSplitListOfPeople: string[] | null,
  ) => void;
}

export default function WhosSplittingDialog(props: Props) {
  enum DialogTabs {
    Groups = "Groups",
    Manual = "Manual"
  }

  const [tab, setTab] = React.useState<DialogTabs>(DialogTabs.Groups);

  const [localManualSplitListOfPeople, setLocalManualSplitListOfPeople] = React.useState<string[] | null>(null);

  // Set up initial state
  useEffect(() => {
    if (props.open) {
      setTab(props.whoToSplitBetween === WhoToSplitBetween.ManualSelection ? DialogTabs.Manual : DialogTabs.Groups);
      setLocalManualSplitListOfPeople(props.manualSplitListOfPeople);
    }
  }, [props.open])

  function selectGroup(group: WhoToSplitBetween) {
    props.onConfirm(group, null);
  }

  function groupsJSX() {
    return (
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={() => selectGroup(WhoToSplitBetween.Everyone)}>
            <ListItemIcon>
              <Avatar src="/everyone.png" />
            </ListItemIcon>
            <ListItemText primary="Everyone" />
            <Box flexGrow={1} />
            {
              props.whoToSplitBetween === WhoToSplitBetween.Everyone &&
              <CheckCircleIcon color="success" />
            }
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => selectGroup(WhoToSplitBetween.SportHouse)}>
            <ListItemIcon>
              <Avatar src="/sport-with-lulu.png" />
            </ListItemIcon>
            <ListItemText primary="Sport climbing house" />
            <Box flexGrow={1} />
            {
              props.whoToSplitBetween === WhoToSplitBetween.SportHouse &&
              <CheckCircleIcon color="success" />
            }
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => selectGroup(WhoToSplitBetween.SportHouseMinusLulu)}>
            <ListItemIcon>
              <Avatar src="/sport.png" />
            </ListItemIcon>
            <ListItemText primary="Sport climbing house (Without Lulu)" />
            <Box flexGrow={1} />
            {
              props.whoToSplitBetween === WhoToSplitBetween.SportHouseMinusLulu &&
              <CheckCircleIcon color="success" />
            }
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => selectGroup(WhoToSplitBetween.BoulderHousePlusLulu)}>
            <ListItemIcon>
              <Avatar src="/bouldering-with-lulu.png" />
            </ListItemIcon>
            <ListItemText primary="Bouldering house (With Lulu)" />
            <Box flexGrow={1} />
            {
              props.whoToSplitBetween === WhoToSplitBetween.BoulderHousePlusLulu &&
              <CheckCircleIcon color="success" />
            }
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => selectGroup(WhoToSplitBetween.BoulderHouse)}>
            <ListItemIcon>
              <Avatar src="/bouldering.png" />
            </ListItemIcon>
            <ListItemText primary="Bouldering house" />
            <Box flexGrow={1} />
            {
              props.whoToSplitBetween === WhoToSplitBetween.BoulderHouse &&
              <CheckCircleIcon color="success" />
            }
          </ListItemButton>
        </ListItem>
      </List>
    )
  }

  function manualJSX() {
    function listForGroupOfPeople(people: string[]) {
      return (
        <List>
          {people.map((person) => (
            <ListItem disablePadding key={person} dense >
              <ListItemButton onClick={() => {
                if (localManualSplitListOfPeople === null) {
                  setLocalManualSplitListOfPeople([person]);
                } else {
                  if (localManualSplitListOfPeople.includes(person)) {
                    setLocalManualSplitListOfPeople(localManualSplitListOfPeople.filter((p) => p !== person));
                  } else {
                    setLocalManualSplitListOfPeople([...localManualSplitListOfPeople, person]);
                  }
                }
              }}>
                <ListItemIcon>
                  <Checkbox
                    checked={localManualSplitListOfPeople?.includes(person) ?? false}
                  />
                </ListItemIcon>
                <ListItemText primary={person} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )
    }

    // A checkbox for each person, and a confirm button
    return (
      <Stack direction="column" >
        <Stack direction="row" spacing={2}>
          {/* Left half, first half of people */}
          {listForGroupOfPeople(AllPeople.slice(0, Math.ceil(AllPeople.length / 2)))}
          {/* Right half, second half of people */}
          {listForGroupOfPeople(AllPeople.slice(Math.ceil(AllPeople.length / 2)))}
        </Stack>
        <Box m={2}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => props.onConfirm(WhoToSplitBetween.ManualSelection, localManualSplitListOfPeople)}
          >
            Done
          </Button>
        </Box>

      </Stack>
    )
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
    >
      <DialogTitle>Who's splitting this expense?</DialogTitle>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
          <Tab value={DialogTabs.Groups} label="Choose a group" />
          <Tab value={DialogTabs.Manual} label="Individual people" />
        </Tabs>
      </Box>

      {
        tab === DialogTabs.Groups ?
          groupsJSX() :
          manualJSX()
      }

    </Dialog>
  )
}
